import useInspectionService from '../../../hooks/inspection-services/useInspectionService.ts';
import useUpdateInspectionServiceMetadata from '../../../hooks/inspection-services/useUpdateInspectionServiceMetadata.ts';
import useUpdateInspectionServiceTags from '../../../hooks/inspection-services/useUpdateInspectionServiceTags.ts';
import useEnableInspectionService from '../../../hooks/inspection-services/useEnableInspectionService.ts';
import usePauseInspectionService from '../../../hooks/inspection-services/usePauseInspectionService.ts';
import useDisableInspectionService from '../../../hooks/inspection-services/useDisableInspectionService.ts';
import {
  UpdateInspectionServiceMetadataRequest,
  UpdateInspectionServiceTagsRequest,
} from '../../../api/requests/inspection-service-request.ts';
import { notifyError } from '../../../utils/NotifyError.tsx';
import { LoadingOverlay, Modal } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import type { ErrorResponse } from '../../../api/responses/error-response.ts';
import MutateInspectionServiceHeaderInfoForm, {
  MutateInspectionServiceHeaderInfoFormValues,
} from '../../ui/places/MutateInspectionServiceHeaderInfoForm.tsx';

interface UpdateInspectionServiceHeaderInfoModalProps {
  opened: boolean;
  onClose: () => void;
  serviceId: string;
  onSuccess?: () => void;
}

const UpdateInspectionServiceHeaderInfoModal = ({
  opened,
  onClose,
  serviceId,
  onSuccess,
}: UpdateInspectionServiceHeaderInfoModalProps) => {
  const { data: inspectionService, isLoading: isInspectionServiceLoading } =
    useInspectionService(serviceId);

  const { mutateAsync: updateInspectionServiceMetadata } =
    useUpdateInspectionServiceMetadata();
  const { mutateAsync: updateInspectionServiceTags } =
    useUpdateInspectionServiceTags();
  const { mutateAsync: enableInspectionService } = useEnableInspectionService();
  const { mutateAsync: pauseInspectionService } = usePauseInspectionService();
  const { mutateAsync: disableInspectionService } =
    useDisableInspectionService();

  const {
    mutate: handleSubmit,
    isPending: isUpdateTagsMetadataAndStatusPending,
  } = useMutation<
    void,
    ErrorResponse,
    MutateInspectionServiceHeaderInfoFormValues
  >({
    mutationFn: async (formValues) => {
      const updateInspectionServiceMetadataRequest: UpdateInspectionServiceMetadataRequest =
        {
          brandId: inspectionService.brandId,
          name: formValues.name,
          notes: inspectionService.notes,
          phoneNumber: inspectionService.phoneNumber,
          pricingSource: inspectionService.pricingSource,
          serviceId: serviceId,
          website: inspectionService.website,
        };

      await updateInspectionServiceMetadata(
        updateInspectionServiceMetadataRequest,
      );

      const updateInspectionServiceTagsRequest: UpdateInspectionServiceTagsRequest =
        {
          serviceId: serviceId,
          tags: formValues.tags,
        };

      await updateInspectionServiceTags(updateInspectionServiceTagsRequest);

      if (formValues.serviceStatus === 'ONLINE') {
        await enableInspectionService(serviceId);
      } else if (formValues.serviceStatus === 'TEMPORARY_OFFLINE') {
        await pauseInspectionService(serviceId);
      } else {
        await disableInspectionService(serviceId);
      }
    },
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      notifyError('更新服務資訊失敗', error.message);
    },
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="編輯驗車資訊"
      centered
      closeOnClickOutside={false}
      size={440}
      styles={{
        title: { fontWeight: 700, fontSize: 16 },
        header: { paddingBottom: 24 },
      }}
    >
      {isInspectionServiceLoading ? (
        <LoadingOverlay visible />
      ) : (
        <MutateInspectionServiceHeaderInfoForm
          initialValues={{
            name: inspectionService.name,
            serviceStatus: inspectionService.serviceStatus,
            tags: inspectionService.tags,
          }}
          onSubmit={handleSubmit}
          onCancel={onClose}
          isSubmitting={isUpdateTagsMetadataAndStatusPending}
        />
      )}
    </Modal>
  );
};

export { UpdateInspectionServiceHeaderInfoModal };
